import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchRecurringInvoices, deleteRecurringInvoice, fetchProfile as fetchProfileAPI } from '../utils/api';
import { formatDate } from "../utils/date";
import { ADMIN_ROLE, FINANCIAL_STAFF } from '../utils/roles';
import CreateRecurringInvoiceModal from './CreateRecurringInvoiceModal';
import './Invoices.css';
import './_Pagination.css';
import './_Buttons.css';
import './_Input.css';
import './_Modal.css';

const RecurringInvoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [sortBy, setSortBy] = useState('created_at');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [profile, setProfile] = useState(null);
    const navigate = useNavigate();

    const getRecurringInvoices = async () => {
        try {
            setLoading(true);
            const response = await fetchRecurringInvoices(sortBy, sortDir, page, limit);
            setInvoices(response.data || []);
            setTotal(response.total || 0);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                const profileData = await fetchProfileAPI();
                setProfile(profileData.data);
                getRecurringInvoices();
            } catch (error) {
                setError(error.message);
            }
        };

        loadData();
    }, [sortBy, sortDir, page, limit]);

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (window.confirm('Are you sure you want to delete this recurring invoice?')) {
            try {
                await deleteRecurringInvoice(id);
                getRecurringInvoices();
            } catch (error) {
                setError(error.message);
            }
        }
    };

    const handleSort = (newSortBy) => {
        setSortDir(sortBy === newSortBy && sortDir === 'asc' ? 'desc' : 'asc');
        setSortBy(newSortBy);
    };

    const handleInvoiceClick = (invoiceId) => {
        navigate(`/recurring-invoices/${invoiceId}`);
    };

    const handleLimitChange = (e) => {
        setLimit(parseInt(e.target.value, 10));
        setPage(1);
    };

    const totalPages = Math.ceil(total / limit) || 1;

    const canManageInvoices = profile && profile.roles.some(role => role.id === ADMIN_ROLE || role.id === FINANCIAL_STAFF);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return (
        <div className="invoices-container">
            <div className="header">
                <h1>Recurring Invoices | {total}</h1>
                <div className="header-controls">
                    <div className="showing-control">
                        <label htmlFor="limit">Showing:</label>
                        <select id="limit" value={limit} onChange={handleLimitChange}>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    {canManageInvoices && <button className="add-student-button" onClick={() => setIsModalOpen(true)}>+</button>}
                </div>
            </div>
            {canManageInvoices && isModalOpen && <CreateRecurringInvoiceModal closeModal={() => setIsModalOpen(false)} refreshData={getRecurringInvoices} />}
            <table className="invoices-table">
                <colgroup>
                    <col style={{ width: '17%' }} />
                    <col style={{ width: '17%' }} />
                    <col style={{ width: '13%' }} />
                    <col style={{ width: '10%' }} />
                    <col style={{ width: '12.5%' }} />
                    <col style={{ width: '8' }} />
                    <col style={{ width: '10.5%' }} />
                    <col style={{ width: '12.5%' }} />
                </colgroup>
                <thead>
                    <tr>
                        <th onClick={() => handleSort('name')}>Name {sortBy === 'name' && (sortDir === 'asc' ? '▲' : '▼')}</th>
                        <th>Student</th>
                        <th onClick={() => handleSort('first_invoice_date')}>First Invoice Date {sortBy === 'first_invoice_date' && (sortDir === 'asc' ? '▲' : '▼')}</th>
                        <th onClick={() => handleSort('end_date')}>End Date {sortBy === 'end_date' && (sortDir === 'asc' ? '▲' : '▼')}</th>
                        <th>Interval</th>
                        <th>Due Days</th>
                        <th>Total Amount</th>
                        {canManageInvoices && <th>Actions</th>}
                    </tr>
                </thead>
                <tbody>
                    {invoices.map((invoice) => (
                        <tr key={invoice.id} onClick={() => handleInvoiceClick(invoice.id)} className="clickable-row">
                            <td>{invoice.name}</td>
                            <td>{invoice.student.first_name} {invoice.student.last_name}</td>
                            <td>{formatDate(invoice.first_invoice_date)}</td>
                            <td>{invoice.end_date ? formatDate(invoice.end_date) : ''}</td>
                            <td>{invoice.interval === 1 ? `Every ${invoice.interval_type.name.slice(0, -1)}` : `Every ${invoice.interval} ${invoice.interval_type.name}`}</td>
                            <td>{invoice.due_days}</td>
                            <td>${(invoice.total_amount / 100).toFixed(2)}</td>
                            {canManageInvoices && <td>
                                <button className='submit-button text-button' onClick={(e) => handleDelete(e, invoice.id)}>Delete</button>
                            </td>}
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={canManageInvoices ? 8 : 7}>
                            <div className="pagination-controls">
                                <div className="page-buttons">
                                    <button onClick={() => handlePageChange(page - 1)} disabled={page === 1}>
                                        {'<'}
                                    </button>
                                    <span>Page {page} of {totalPages}</span>
                                    <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>
                                        {'>'}
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

export default RecurringInvoices;
